<template>
    <div class="dashboard-info-page">

        <section class="welcome-card">
            <div>
                <span class="badge">Portal do Estabelecimento</span>

                <h1>Bem-vindo ao SGSIM</h1>

                <p>
                    Utilize este ambiente para acompanhar informações do estabelecimento,
                    consultar documentos, verificar registros e manter os dados sempre atualizados.
                </p>
            </div>
        </section>

        <section v-if="pendenciasAtivas.length" class="pendencias-card">
            <div class="pendencias-title">
                <strong>Ações pendentes</strong>
                <span>Itens aguardando sua atenção</span>
            </div>

            <div class="pendencias-grid">
                <div 
                    v-for="item in pendenciasAtivas" 
                    :key="item.key"
                    class="pendencia-item"
                >
                    <div class="pendencia-label">
                        <span>{{ item.icon }}</span>
                        <strong>{{ item.titulo }}</strong>
                    </div>

                    <div class="pendencia-total">
                        {{ item.total }}
                    </div>
                </div>
            </div>
        </section>

        <section class="info-grid">

            <div class="info-card">
                <div class="icon">📄</div>
                <h3>Documentos e anexos</h3>
                <p>
                    Consulte os documentos vinculados ao estabelecimento, acompanhe
                    vencimentos e mantenha os anexos sempre atualizados.
                </p>
            </div>

            <div class="info-card">
                <div class="icon">📝</div>
                <h3>Registros e informações</h3>
                <p>
                    Mantenha os dados cadastrais corretos e acompanhe as informações
                    relacionadas às atividades do estabelecimento.
                </p>
            </div>

            <div class="info-card">
                <div class="icon">🔍</div>
                <h3>Consultas</h3>
                <p>
                    Utilize as ferramentas de consulta para localizar documentos,
                    registros e informações do processo de inspeção.
                </p>
            </div>

            <div class="info-card">
                <div class="icon">📌</div>
                <h3>Acompanhamento</h3>
                <p>
                    Verifique regularmente as informações disponíveis no sistema
                    para manter o acompanhamento das atividades e procedimentos.
                </p>
            </div>

        </section>
    </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import { Api } from '@/funcoes/apiExport';
import { globalStore } from '@/globals';

const pendencias = ref({
    ttassinaturas: 0,
    ttprocessos: 0,
    ttrac: 0,
    ttdocumentos: 0,
});

const pendenciasAtivas = computed(() => {
    const itens = [
        {
            key: 'ttassinaturas',
            total: pendencias.value.ttassinaturas,
            icon: '✍️',
            titulo: 'Assinaturas',
        },
        {
            key: 'ttprocessos',
            total: pendencias.value.ttprocessos,
            icon: '📌',
            titulo: 'Processos',
        },
        {
            key: 'ttrac',
            total: pendencias.value.ttrac,
            icon: '🛠️',
            titulo: 'Ações corretivas',
        },
        {
            key: 'ttdocumentos',
            total: pendencias.value.ttdocumentos,
            icon: '📄',
            titulo: 'Documentos',
        },
    ];

    return itens.filter(item => Number(item.total) > 0);
});

async function lerDados(){
    try{
        const req = await Api.genericPost('/dados/usuario/resumo/pendencias', {
            idep: globalStore.idep,
            idsim: globalStore.idsim,
            idestabelecimento: globalStore.idestabelecimento,
            idusuario: globalStore.idusuario,
        });

        pendencias.value = Array.isArray(req) && req.length ? req[0] : {
            ttassinaturas: 0,
            ttprocessos: 0,
            ttrac: 0,
            ttdocumentos: 0,
        };
    }catch(error){
        console.error('Erro ao carregar pendências:', error);
    }
}

onMounted(lerDados);
</script>

<style scoped>
.dashboard-info-page {
    padding:14px 20px;
}

.welcome-card {
    background:linear-gradient(135deg,#f8fafc,#eef2ff);
    border:1px solid #dbe3f0;
    border-radius:12px;
    padding:22px 24px;
    margin-bottom:12px;
    box-shadow:0 3px 10px rgba(15,23,42,.05);
    text-align:center;
}

.badge{
    display:inline-block;
    background:#e0e7ff;
    color:#3730a3;
    font-size:12px;
    font-weight:700;
    padding:5px 10px;
    border-radius:999px;
    margin-bottom:10px;
}

.welcome-card h1{
    margin:0 0 8px;
    color:#1f2937;
    font-size:25px;
    line-height:1.15;
}

.welcome-card p{
    margin:0 auto;
    color:#111827;
    line-height:1.4;
    max-width:1100px;
    font-size:14px;
    font-weight:600;
}

.pendencias-card{
    display:flex;
    align-items:center;
    gap:14px;

    background:#fff7ed;
    border:1px solid #fed7aa;
    border-radius:12px;

    padding:10px 12px;
    margin-bottom:20px;

    box-shadow:0 2px 8px rgba(15,23,42,.04);
}

.pendencias-title{
    min-width:190px;
    text-align:left;
}

.pendencias-title strong{
    display:block;
    color:#7c2d12;
    font-size:15px;
}

.pendencias-title span{
    display:block;
    color:#9a3412;
    font-size:12px;
    margin-top:2px;
}

.pendencias-grid{
    flex:1;
    display:grid;
    grid-template-columns:repeat(auto-fit,minmax(180px,1fr));
    gap:8px;
}

.pendencia-item{
    display:flex;
    align-items:center;
    justify-content:space-between;
    gap:8px;

    background:#fff;
    border:1px solid #fed7aa;
    border-radius:9px;

    padding:8px 10px;
    min-height:42px;
    box-sizing:border-box;
}

.pendencia-label{
    display:flex;
    align-items:center;
    gap:7px;
    min-width:0;
}

.pendencia-label span{
    font-size:17px;
    flex-shrink:0;
}

.pendencia-label strong{
    color:#1f2937;
    font-size:13px;
    white-space:nowrap;
    overflow:hidden;
    text-overflow:ellipsis;
}

.pendencia-total{
    min-width:26px;
    height:26px;
    border-radius:999px;

    background:#ea580c;
    color:#fff;

    display:flex;
    align-items:center;
    justify-content:center;

    font-weight:700;
    font-size:12px;
    flex-shrink:0;
}

.info-grid{
    display:grid;
    grid-template-columns:repeat(auto-fit,minmax(240px,1fr));
    gap:16px;
}

.info-card{
    background:#fff;
    border:1px solid #e5e7eb;
    border-radius:12px;
    padding:24px 20px;
    box-shadow:0 2px 8px rgba(15,23,42,.04);
    transition:.2s;
    text-align:center;
    min-height:185px;
    box-sizing:border-box;
}

.info-card:hover{
    transform:translateY(-3px);
}

.icon{
    font-size:30px;
    margin-bottom:14px;
}

.info-card h3{
    margin:0 0 10px;
    color:#1f2937;
    font-size:17px;
}

.info-card p{
    margin:0 auto;
    color:#6b7280;
    line-height:1.45;
    font-size:14px;
    max-width:320px;
}

@media (max-width:900px){
    .pendencias-card{
        align-items:stretch;
        flex-direction:column;
    }

    .pendencias-title{
        min-width:0;
    }
}

@media (max-width:600px){
    .dashboard-info-page{
        padding:10px;
    }

    .welcome-card{
        padding:18px 16px;
    }

    .welcome-card h1{
        font-size:22px;
    }

    .info-grid{
        grid-template-columns:1fr;
    }
}
</style>